import React, { useEffect, useRef, useState } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  XAxis,
  YAxis
} from 'recharts';
import PropTypes from 'prop-types';
import CustomizedXAxisTick from './CustomizedXAxisTick';
import CustomizedYAxisTick from './CustomizedYAxisTick';
import { withTranslation } from 'react-i18next';
import _ from 'lodash';
import EmbedChartModal from '../EmbedChartModal';
import domtoimage from 'dom-to-image';
import fileDownload from 'js-file-download';
import { LazyLoadImage } from 'react-lazy-load-image-component';

const PriceChart = props => {
  const {
    isBrand,
    isSingleElement,
    t,
    drugName,
    priceData,
    lineKeys,
    quantity,
    dosage,
    displayKeys,
    isModalButton,
    dynamicChartData,
    seoName,
    i18n,
    form
  } = props;

  const isEs = _.isEqual(i18n.language, 'es');
  const chartRef = useRef(null);
  const tooltip = useRef(null);
  const tooltipQty = useRef(null);
  const tooltipArrow = useRef(null);
  const [embedModal, setEmbedModal] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [descGraph, setdescGraph] = useState(false);

  useEffect(() => {
    let text = '';
    if (!_.isEmpty(priceData) && priceData.length > 1) {
      let priceDataVal = priceData[priceData.length - 1];
      const xVal = priceDataVal[lineKeys[isBrand ? 1 : 0]];
      priceDataVal = priceData[priceData.length - 1];
      const yVal = priceDataVal[lineKeys[isBrand ? 2 : 1]];
      text = isBrand
        ? t('Graph 3 avg value', { drugName: drugName, xVal: xVal, yVal: yVal })
        : t('Graph 2 avg value', {
            drugName: drugName,
            xVal: xVal,
            yVal: yVal
          });
    }
    setdescGraph(text);
  }, [priceData, lineKeys, drugName]);

  const handleDownload = () => {
    domtoimage
      .toBlob(document.getElementById('priceGraph'), {
        bgcolor: '#ffffff',
        width: chartRef.current.scrollWidth + 75,
        height: chartRef.current.scrollHeight + 75,
        style: {
          padding: '10px'
        }
      })
      .then(function (blob) {
        fileDownload(blob, `${props.seoName}-singlecare-price-history.png`);
      });
  };

  const customMouseOver = e => {
    let tooltipLabel = 'Generic';
    if (e.dataKey?.includes('brand')) {
      tooltipLabel = 'Brand';
    }
    if (e.dataKey?.includes('genericsinglecareprice') && isSingleElement) {
      tooltipLabel = 'Brand';
    }
    if (e.dataKey?.includes('genericsinglecareprice') && isSingleElement) {
      tooltipLabel = 'Brand';
    }
    if (!e.dataKey?.includes('singlecareprice')) {
      tooltip.current.style.background = '#565a5c';
      tooltipArrow.current.style.borderTop = '6px solid #565a5c';
      tooltipQty.current.style.color = '#ffffff';
    } else {
      tooltip.current.style.background = e.fill;
      tooltipArrow.current.style.borderTop = `6px solid ${'#dd216b'}`;
      tooltipQty.current.style.color = '#ffffff';
    }
    let x = Math.round(e.cx);
    let y = Math.round(e.cy);
    tooltip.current.style.opacity = '1';
    tooltip.current.style.transform = `translate(${x - 35}px, ${y - 62}px)`;
    tooltipQty.current.innerHTML = `<span class="tooltipValue">$${Number(
      e.value
    ).toFixed(2)}</span> <span class="tooltipName">${tooltipLabel}</span>`;
  };

  const over = () => {
    tooltip.current.style.opacity = '0';
  };

  const renderLegend = filterFlag => {
    let colorNameArray = [
      t('Dark grey line'),
      t('Grey dotted line'),
      t('Violet line')
    ];
    const updatedKeys = filterFlag
      ? displayKeys.filter(item => !item.value.includes('Single'))
      : displayKeys;
    let totalLines = updatedKeys && updatedKeys?.length;
    return (
      <>
        <ul
          className="recharts-default-legend"
          style={{
            margin: '0',
            textAlign: 'left'
          }}
        >
          {updatedKeys.map((entry, index) => {
            let ariaName = colorNameArray[index];
            if (totalLines == 2 && index == 1) {
              ariaName = colorNameArray[index + 1];
            }
            return (
              <li
                className={`recharts-legend-item legend-item-${index}`}
                key={`item-${index}`}
                style={{ display: 'inline-block', marginRight: '10px' }}
              >
                <svg
                  aria-label={ariaName}
                  className="recharts-surface"
                  width="10"
                  height="10"
                  style={{
                    display: 'inline-block',
                    verticalAlign: 'middle',
                    marginRight: '10px'
                  }}
                  viewBox="0"
                  version="1.1"
                >
                  <path
                    strokeWidth="1"
                    fill={entry.color}
                    stroke={entry.color}
                    d="m5 9c-2.2 0-4-1.8-4-4 0-2.2 1.8-4 4-4 2.2 0 4 1.8 4 4 0 2.2-1.8 4-4 4z"
                    className="recharts-legend-icon"
                  ></path>
                </svg>
                <span className="recharts-legend-item-text">{entry.value}</span>
              </li>
            );
          })}
        </ul>
        {!isEs && (isModalButton ? false : !embedModal) && (
          <button
            className="embedButtons"
            onClick={() => {
              setEmbedModal(true);
            }}
          >
            Embed this chart
          </button>
        )}
        {(isModalButton ? true : embedModal) && (
          <a
            className="logoWrap"
            href={`${window.location.origin}/prescription/${seoName}#Price-History`}
          >
            Price history provided by
            <LazyLoadImage
              src="/content/images/header/singlecare-menu.png"
              width="100"
              height="16"
              alt="SingleCare"
            />
          </a>
        )}
      </>
    );
  };

  const closeRequestModal = () => {
    setEmbedModal(false);
  };
  return (
    <section>
      <div className="chart-wrapper">
        {!embedModal && (
          <>
            <h3 id="priceHistoryLabel">
              {t('Price History for')} {drugName && drugName}
            </h3>
            <span className="qty-txt">
              {quantity} {t('count1')}, {form}, {dosage}
            </span>
          </>
        )}
      </div>
      {embedModal && (
        <EmbedChartModal
          closeDialogHandler={closeRequestModal}
          exportChart={handleDownload}
          dynamicChartData={dynamicChartData}
          drugName={seoName}
        />
      )}
      <section className={`chardSection ${embedModal ? 'embedMargin' : ''}`}>
        <div className="chart-wrapper">
          <div className="custom-tooltip" ref={tooltip}>
            <div ref={tooltipQty} className="label" />
            <span ref={tooltipArrow} className="down-arrow"></span>
          </div>
          <div className="priceChart" ref={chartRef} id="priceGraph">
            {embedModal && (
              <>
                <h3 id="priceHistoryLabel">
                  {t('Price History for')} {drugName && drugName}
                </h3>
                <span className="qty-txt">
                  {quantity} {t('count1')}, {form}, {dosage}
                </span>
              </>
            )}
            <ResponsiveContainer width={'100%'} height={'100%'}>
              <LineChart
                role="img"
                aria-labelledby="priceHistoryLabel"
                data={priceData}
                margin={{
                  top: 15,
                  right: 0,
                  left: 0,
                  bottom: 0
                }}
              >
                <CartesianGrid vertical={false} />
                {/* <desc id="graphDescriptionID">{descGraph}</desc> */}
                <XAxis
                  dataKey="name"
                  interval={0}
                  /*label={{
                    fill: "#532b9f",
                    value: "Month",
                    position: "bottom",
                    fontWeight: 600,
                    paddingLeft: "30",
                    dy: 15
                  }}*/
                  axisLine={false}
                  padding={{ left: 10, right: 20 }}
                  tick={<CustomizedXAxisTick />}
                />
                <YAxis
                  type="number"
                  /*label={{
                    fill: "#532b9f",
                    value: "Cost in USD",
                    angle: -90,
                    position: "insideLeft",
                    fontWeight: 600,
                    dx: 10,
                    dy: 15
                  }}*/
                  axisLine={false}
                  tick={<CustomizedYAxisTick />}
                />
                <Legend
                  align="left"
                  iconSize={14}
                  height={15}
                  fontSize="11px"
                  verticalAlign="bottom"
                  fontFamily="felix-semibold"
                  wrapperStyle={{
                    //position: "block"
                    // paddingLeft: "38px",
                    // paddingBottom: "0px"
                    paddingTop: '20px'
                  }}
                  payload={
                    embedModal
                      ? displayKeys.filter(
                          item => !item.value.includes('Single')
                        )
                      : displayKeys
                  }
                  content={
                    embedModal
                      ? () => renderLegend(true)
                      : () => renderLegend(false)
                  }
                />
                <Line
                  type="linear"
                  dataKey={lineKeys[0]}
                  stroke="#4a474f"
                  fill="#4a474f"
                  connectNulls={true}
                  dot={{
                    onMouseOver: e => customMouseOver(e),
                    onMouseLeave: over
                  }}
                  strokeWidth="2"
                />
                {(isBrand ? true : !embedModal) && (
                  <Line
                    type="linear"
                    dataKey={lineKeys[1]}
                    stroke={isBrand ? '#716c78' : '#dd216b'}
                    fill={isBrand ? '#716c78' : '#dd216b'}
                    strokeWidth="2"
                    connectNulls={true}
                    strokeDasharray={isBrand ? '3 3' : ''}
                    dot={{
                      onMouseOver: e => customMouseOver(e),
                      onMouseLeave: over,
                      strokeDasharray: '0 0'
                    }}
                  />
                )}
                {isBrand && !embedModal ? (
                  <Line
                    type="linear"
                    dataKey={lineKeys[2]}
                    stroke="#dd216b"
                    fill="#dd216b"
                    strokeWidth="2"
                    connectNulls={true}
                    dot={{
                      onMouseOver: e => customMouseOver(e),
                      onMouseLeave: over
                    }}
                  />
                ) : null}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>
    </section>
  );
};

PriceChart.propTypes = {
  drugName: PropTypes.string,
  isBrand: PropTypes.bool,
  lineKeys: PropTypes.array,
  displayKeys: PropTypes.array,
  priceData: PropTypes.array,
  quantity: PropTypes.string,
  dosage: PropTypes.string,
  dynamicChartData: PropTypes.object,
  seoName: PropTypes.string
};

PriceChart.defaultProps = {
  lineKeys: [],
  drugName: '',
  isBrand: false,
  displayKeys: [],
  priceData: [],
  quantity: '',
  dosage: '',
  dynamicChartData: {},
  seoName: ''
};
export default withTranslation()(PriceChart);
